import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SpaceWrapper } from "../../components";
import {
  DateRangePicker,
  Button,
  StatsContainer,
  StatsMatrix,
  ChartContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  ChartTooltip,
  ChartTooltipContent,
  Skeleton,
  DataTable,
  AddressInputField,
  NotificationCard,
} from "@/components";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PiPresentationFill } from "react-icons/pi";
import { MdNotificationAdd } from "react-icons/md";
import { X } from "lucide-react";
import ColumnHelper from "@/utils/ColumnHelper";
import { RiFileExcel2Line } from "react-icons/ri";
import BannerForm from "../Banner/BannerForm";
import { useDispatch, useSelector } from "react-redux";
import { subDays } from "date-fns";
import {
  addAllEscalationsIO,
  addAllNDRsIO,
  addAllNotifications,
  addAppData,
  addBookedOrdersIO,
  addCodRemittance,
  addSettings,
  addShipmentsIO,
  addUser,
  fetchDashboardData,
  selectAllNotifications,
} from "../../app/Slices";
import { TfiStatsUp } from "react-icons/tfi";
import { FaBox } from "react-icons/fa6";
import NotificationForm from "../Notification/NotificationForm";
import UploadForm from "./UploadForm";
import { IoImages } from "react-icons/io5";

const chartConfig = {
  Distribution: { label: "Distribution", color: "#9DD257" },
};

// Reusable Chart Section
const ChartSection = ({ chartData }) => (
  <div className="flex flex-col gap-y-2 w-full md:w-1/2 bg-background_2/90 rounded-xl p-3">
    <ChartContainer
      layout="vertical"
      config={chartConfig}
      className="h-[200px] w-full"
    >
      <BarChart accessibilityLayer data={chartData}>
        <CartesianGrid vertical={true} className={"opacity-40"} />
        <XAxis
          dataKey="Zone"
          tickLine={false}
          tickMargin={10}
          axisLine={false}
        />
        <ChartTooltip content={<ChartTooltipContent />} />
        <Bar dataKey="Shipments" fill="#9DD257" radius={4} />
      </BarChart>
    </ChartContainer>
  </div>
);

// Reusable Notification Skeleton
const NotificationSkeleton = () => (
  <div className="flex items-center space-x-4 bg-background rounded-xl p-3">
    <Skeleton className="h-12 w-12 rounded-xl bg-background_2/90" />
    <div className="space-y-2">
      <Skeleton className="h-4 w-[150px] bg-background_2/90" />
      <Skeleton className="h-4 w-[100px] bg-background_2/90" />
    </div>
  </div>
);
const Dashboard = () => {
  const data = useSelector((state) => state.dashboard.data);
  const Shipments = data?.shipments || {};
  const totalFreightCharge = Shipments?.totalFreightCharge || 0;
  const totalCodCharges = Shipments?.totalCodCharges || 0;
  const totalRevenue = totalCodCharges + totalFreightCharge || 0;
  const NDR = data?.ndr || {};
  const COD = data?.cod || {};
  const zoneDistribution = data?.zoneDistribution || {};
  const courierPerformance = data?.courierPerformance || {};
  const customerPerformance = data?.customerPerformance || {};
  const [alertOpen, setAlertOpen] = useState(false);
  const [uploadAlertOpen, setUploadAlertOpen] = useState(false);
  const [notificationAlertOpen, setNotificationAlertOpen] = useState(false);

  const [notifications, setNotifications] = useState([]);

  const notificationsData = useSelector(selectAllNotifications) || [];
  const notificationLoading =
    useSelector((state) => state.notification?.loading) || false;
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);
  useEffect(() => {
    if (notificationsData.length > 0) {
      // Take only the 2 most recent notifications
      const recentNotifications = notificationsData.slice(0, 2);

      setNotifications(
        recentNotifications?.map((notification) => ({
          id: notification._id, // Use _id from API response
          image: notification.content?.imageUrl || "", // Ensure it doesn't break if imageUrl is missing
          title: notification.content?.title || "No Title",
          date: formatDate(notification.createdAt),
          description: notification.content?.message || "No Description",
        }))
      );
    }
  }, [notificationsData]);

  const courierColumns = useMemo(
    () => [
      ColumnHelper({
        header: "Rank",
        accessorKey: "rank",
        meta: {
          tooltip: (row) => `Rank: ${row.rank || "N/A"}`,
        },
        width: "50px",
      }),
      ColumnHelper({
        header: "Courier Name",
        accessorKey: "name",
        meta: {
          tooltip: (row) => row.name || "N/A",
        },
      }),
      ColumnHelper({
        header: "Total Shipment",
        accessorKey: "totalShipments",
        meta: {
          tooltip: (row) => `Total: ${row.totalShipments || 0}`,
        },
      }),
      ColumnHelper({
        header: "Pending",
        accessorKey: "pending",
        meta: {
          tooltip: (row) => `Pending: ${row.pending || 0}`,
        },
      }),
      ColumnHelper({
        header: "In - Transit",
        accessorKey: "inTransit",
        meta: {
          tooltip: (row) => `In-Transit: ${row.inTransit || 0}`,
        },
      }),
      ColumnHelper({
        header: "Out for Delivery",
        accessorKey: "outForDelivery",
        meta: {
          tooltip: (row) => `Out for Delivery: ${row.outForDelivery || 0}`,
        },
      }),
      ColumnHelper({
        header: "Delivered",
        accessorKey: "delivered",
        meta: {
          tooltip: (row) => `Delivered: ${row.delivered || 0}`,
        },
      }),
      ColumnHelper({
        header: "RTO",
        accessorKey: "rto",
        meta: {
          tooltip: (row) => `RTO: ${row.rto || 0}`,
        },
      }),
      ColumnHelper({
        header: "Lost/Damage",
        accessorKey: "lostDamaged",
        meta: {
          tooltip: (row) => `Lost/Damage: ${row.lostDamaged || 0}`,
        },
      }),
    ],
    []
  );
  const customerColumns = useMemo(
    () => [
      ColumnHelper({
        header: "Rank",
        accessorKey: "rank",
        cell: ({ row }) => row.index + 1,
        meta: {
          tooltip: (row) => `Rank: ${row.index + 1}`,
        },
        width: "50px",
      }),
      ColumnHelper({
        header: "Customer Name",
        accessorKey: "name",
        meta: {
          tooltip: (row) => row.name || "N/A",
        },
      }),
      ColumnHelper({
        header: "Customer Id",
        accessorKey: "clientId",
        meta: {
          tooltip: (row) => row.clientId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Customer Email",
        accessorKey: "email",
        meta: {
          tooltip: (row) => row.email || "N/A",
        },
      }),
      ColumnHelper({
        header: "Total Shipment",
        accessorKey: "totalShipments",
        meta: {
          tooltip: (row) => `Total: ${row.totalShipments || 0}`,
        },
      }),
      ColumnHelper({
        header: "Pending",
        accessorKey: "pending",
        meta: {
          tooltip: (row) => `Pending: ${row.pending || 0}`,
        },
      }),
      ColumnHelper({
        header: "In - Transit",
        accessorKey: "inTransit",
        meta: {
          tooltip: (row) => `In-Transit: ${row.inTransit || 0}`,
        },
      }),
      ColumnHelper({
        header: "Out for Delivery",
        accessorKey: "outForDelivery",
        meta: {
          tooltip: (row) => `Out for Delivery: ${row.outForDelivery || 0}`,
        },
      }),
      ColumnHelper({
        header: "Delivered",
        accessorKey: "delivered",
        meta: {
          tooltip: (row) => `Delivered: ${row.delivered || 0}`,
        },
      }),
      ColumnHelper({
        header: "RTO",
        accessorKey: "rto",
        meta: {
          tooltip: (row) => `RTO: ${row.rto || 0}`,
        },
      }),
      ColumnHelper({
        header: "Lost/Damage",
        accessorKey: "lostDamaged",
        meta: {
          tooltip: (row) => `Lost/Damage: ${row.lostDamaged || 0}`,
        },
      }),
    ],
    []
  );

  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};

  // Using the entity adapter selector

  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 10 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;

  const [date, setDate] = useState({
    from: startOfMonthDate,
    to: endOfMonthDate,
  });

  const DeliveryInfoData = [
    {
      type: "icon",
      title: "Total Revenue",
      subtitle: "Total value of delivered orders (in Rs.)",
      icon: TfiStatsUp,
      value: totalRevenue.toFixed(2).toLocaleString("en-IN"),
    },
    {
      type: "icon",
      title: "Avg. Order Cost ",
      subtitle: "Your average shipping cost of all orders",
      icon: FaBox,
      value:
        Shipments?.totalShipments > 0
          ? (Shipments.totalFreightCharge / Shipments.totalShipments)
              .toFixed(2)
              .toLocaleString("en-IN")
          : "0.00",
    },
    {
      type: "progress",
      title: "Delivered Percentage ",
      subtitle: "Total orders delivered (in percentage)",
      current: Shipments.delivered,
      total:
        Shipments?.pending +
          Shipments?.delivered +
          Shipments?.inTransit +
          Shipments?.outForDelivery || 0,
      label: "orders delivered",
    },
  ];
  const chartData = [
    { Zone: "Zone 1", Shipments: zoneDistribution?.zone1 },
    { Zone: "Zone 2", Shipments: zoneDistribution?.zone2 },
    { Zone: "Zone 3", Shipments: zoneDistribution?.zone3 },
    { Zone: "Zone 4", Shipments: zoneDistribution?.zone4 },
    { Zone: "Zone 5", Shipments: zoneDistribution?.zone5 },
    { Zone: "Zone 6", Shipments: zoneDistribution?.zone6 },
  ];

  const StatsShipment = [
    {
      statusName: "Total Shipments",
      statusNumber: Shipments?.totalShipments || 0,
    },
    {
      statusName: "Pending",
      statusNumber: Shipments.pending || 0,
    },
    {
      statusName: "In Transit",
      statusNumber: Shipments.inTransit || 0,
    },
    {
      statusName: "Out for Delivery",
      statusNumber: Shipments.outForDelivery || 0,
    },
    {
      statusName: "Delivered",
      statusNumber: Shipments.delivered || 0,
    },
  ];

  const StatsNdr = [
    { statusName: "Total NDR", statusNumber: NDR?.totalNdr || 0 },
    {
      statusName: "Action Requires",
      statusNumber: NDR?.actionRequires || 0,
    },
    {
      statusName: "Action Requested",
      statusNumber: NDR?.actionRequested || 0,
    },
    {
      statusName: "Delivered",
      statusNumber: NDR?.delivered || 0,
    },
    {
      statusName: "RTO",
      statusNumber: NDR?.rto || 0,
    },
  ];

  const StatsCod = [
    { statusName: "Total Remitted", statusNumber: COD?.totalRemittance },
    { statusName: "Last Remittance", statusNumber: COD?.lastRemittance },
    {
      statusName: "Next Remittance",
      statusNumber: (COD?.totalRemittance * 0.3).toFixed(2),
    },
    { statusName: "Total Remitted Due", statusNumber: COD?.totalDue },
  ];

  const handleDateChange = useCallback(
    (dateRange) => {
      setDate({
        from: dateRange?.from || "",
        to: dateRange?.to || "",
      });
    },
    [startOfMonthDate, endOfMonthDate]
  );

  const dispatch = useDispatch();
  const isDashboardDataLoading = useSelector(
    (state) => state.dashboard.loading
  );
  const prevDateRef = useRef(date);

  const fetchData = async () => {
    if (!date.from || !date.to) return;
    if (isDashboardDataLoading) return;

    // Check if date range has actually changed (skip for initial mount)
    if (
      prevDateRef.current.from?.getTime() === date.from?.getTime() &&
      prevDateRef.current.to?.getTime() === date.to?.getTime() &&
      data
    ) {
      return;
    }

    const response = await dispatch(
      fetchDashboardData({
        startDate: date.from,
        endDate: date.to,
      })
    );

    if (fetchDashboardData.fulfilled.match(response)) {
      console.log(
        "[Date] Dashboard Data fetched successfully :-",
        response.payload
      );
    } else if (fetchDashboardData.rejected.match(response)) {
      console.log("Data fetch failed :- ", response.payload);
    }

    // Update the previous date reference
    prevDateRef.current = date;
  };

  useEffect(() => {
    fetchData();
  }, [date, dispatch]);

  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-4 p-4 pb-20 md:pb-4 relative max-w-[2000px]">
        <div className="flex flex-col md:flex-row gap-2 w-full md:gap-4 justify-between">
          <DateRangePicker s value={date} onChange={handleDateChange} />
          <div className="flex gap-2">
            <AlertDialog
              open={uploadAlertOpen}
              onOpenChange={setUploadAlertOpen}
            >
              <AlertDialogTrigger asChild>
                <Button
                  icon={IoImages}
                  className="h-12"
                  size="md"
                  onClick={() => console.log("Add Banner")}
                >
                  Upload
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="!bg-background !rounded-xl !w-full !h-fit ">
                <button
                  onClick={() => setUploadAlertOpen(false)}
                  className="flex absolute right-4 top-4 justify-center !h-6 !w-6 !grow-0 !shrink-0 items-center rounded-md bg-background_2/60 hover:bg-primary/30 transition-all"
                >
                  <X className="!h-3 text-secondary/60" />
                </button>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-nunito !text-secondary/90 !font-bold !text-headline4 md:!!text-headline3 !ml-2 flex justify-between items-center">
                    Upload Images
                  </AlertDialogTitle>
                  <AlertDialogDescription className="!w-full flex flex-col"></AlertDialogDescription>
                </AlertDialogHeader>
                <UploadForm setAlertOpen={setUploadAlertOpen} />
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={alertOpen} onOpenChange={setAlertOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  icon={PiPresentationFill}
                  className="h-12"
                  size="md"
                  onClick={() => console.log("Add Banner")}
                >
                  Add Banner
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="!bg-background !rounded-xl !w-full !h-fit ">
                <button
                  onClick={() => setAlertOpen(false)}
                  className="flex absolute right-4 top-4 justify-center !h-6 !w-6 !grow-0 !shrink-0 items-center rounded-md bg-background_2/60 hover:bg-primary/30 transition-all"
                >
                  <X className="!h-3 text-secondary/60" />
                </button>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-nunito !text-secondary/90 !font-bold !text-headline4 md:!!text-headline3 !ml-2 flex justify-between items-center">
                    Add Banner
                  </AlertDialogTitle>

                  <AlertDialogDescription className="!w-full flex flex-col"></AlertDialogDescription>
                </AlertDialogHeader>
                <BannerForm setAlertOpen={setAlertOpen} />
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
              open={notificationAlertOpen}
              onOpenChange={setNotificationAlertOpen}
            >
              <AlertDialogTrigger asChild>
                <Button
                  icon={MdNotificationAdd}
                  className="h-12"
                  size="md"
                  onClick={() => console.log("Add Notification")}
                >
                  Add Notification
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="!bg-background !rounded-xl !w-full">
                <button
                  onClick={() => setNotificationAlertOpen(false)}
                  className="flex absolute right-4 top-4 justify-center !h-6 !w-6 !grow-0 !shrink-0 items-center rounded-md bg-background_2/60 hover:bg-primary/30 transition-all"
                >
                  <X className="!h-3 text-secondary/60" />
                </button>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-nunito !text-secondary/90 !font-bold !text-headline4 md:!!text-headline3 !ml-2">
                    Add Notification
                  </AlertDialogTitle>
                  <AlertDialogDescription className="!w-full flex flex-col"></AlertDialogDescription>
                </AlertDialogHeader>
                <NotificationForm
                  setNotificationAlertOpen={setNotificationAlertOpen}
                />
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        <StatsContainer status={StatsShipment} label="Shipment Details" />
        <StatsContainer status={StatsNdr} label="NDR Details" />
        <StatsContainer status={StatsCod} label="COD Status" lgCols={4} />
        <StatsMatrix cards={DeliveryInfoData} />
        <div className="flex flex-col md:flex-row gap-2 w-full md:gap-4">
          <ChartSection chartData={chartData} />
          <div className="bg-background_2/90 rounded-xl p-3 flex flex-col gap-2 w-full md:w-1/2">
            <h2 className="md:text-headline4 text-subtitle1 font-nunito font-bold text-secondary/95">
              Recent Notifications
            </h2>

            <div className="flex flex-col gap-4">
              {notificationLoading ? (
                <>
                  <NotificationSkeleton />
                  <NotificationSkeleton />
                </>
              ) : notifications.length > 0 ? (
                notifications.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    image={notification.image}
                    title={notification.title}
                    date={notification.date}
                    description={notification.description}
                  />
                ))
              ) : (
                <p className="text-center text-gray-500 mt-4">
                  No notifications available
                </p>
              )}
            </div>
          </div>
        </div>
        <DataTable
          columns={courierColumns}
          tittle="Courier Performance"
          data={courierPerformance || []}
          showCheckbox={false}
          actions={[
            {
              name: "Export",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Admin: Courier Performance",
                filename: `Courier_Performance${
                  new Date().toISOString().split("T")[0]
                }`,
                date: date,
              },
            },
          ]}
        />
        <DataTable
          columns={customerColumns}
          tittle="Customer Performance"
          data={customerPerformance || []}
          showCheckbox={false}
          actions={[
            {
              name: "Export",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Admin: Customer Performance",
                filename: `Customer_Performance${
                  new Date().toISOString().split("T")[0]
                }`,
                date: date,
              },
            },
          ]}
        />
      </div>
    </SpaceWrapper>
  );
};

export default Dashboard;
